/**
 * Sample workspace for the Google OAuth verification recording.
 * Display-only: it never writes the cast back onto the signed-in profile.
 */

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export const VERIFICATION_STUDIO_STORAGE_KEY = 'flowstate:verification-studio';

interface VerificationStudioValue {
  enabled: boolean;
  enable: () => void;
  disable: () => void;
}

const VerificationStudioContext = createContext<VerificationStudioValue | null>(null);

function readStoredEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get('studio') === '1') {
    localStorage.setItem(VERIFICATION_STUDIO_STORAGE_KEY, 'true');
    return true;
  }
  if (params.get('studio') === '0') {
    localStorage.setItem(VERIFICATION_STUDIO_STORAGE_KEY, 'false');
    return false;
  }
  return localStorage.getItem(VERIFICATION_STUDIO_STORAGE_KEY) === 'true';
}

export function VerificationStudioProvider({ children }: { children: React.ReactNode }) {
  // Lazy initializer so the cast is in place on the first client render — otherwise a
  // reload mid-recording paints the signed-in profile for a frame.
  const [enabled, setEnabled] = useState(readStoredEnabled);

  const enable = useCallback(() => {
    localStorage.setItem(VERIFICATION_STUDIO_STORAGE_KEY, 'true');
    setEnabled(true);
  }, []);

  const disable = useCallback(() => {
    localStorage.setItem(VERIFICATION_STUDIO_STORAGE_KEY, 'false');
    setEnabled(false);
  }, []);

  const value = useMemo(() => ({ enabled, enable, disable }), [enabled, enable, disable]);

  return (
    <VerificationStudioContext.Provider value={value}>
      {children}
    </VerificationStudioContext.Provider>
  );
}

const DISABLED: VerificationStudioValue = {
  enabled: false,
  enable: () => {},
  disable: () => {},
};

export function useVerificationStudio(): VerificationStudioValue {
  return useContext(VerificationStudioContext) ?? DISABLED;
}
